import { createEffect, createSignal, onCleanup, For, onMount, Show } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import { useNavigate } from "@solidjs/router";
import gsap from "gsap";
import { setGlobalBackground } from "../../../state";
import { Row, Column } from "@lightningtv/solid-ui";
import { LazyUp } from "@lightningtv/solid/primitives";
import LoadingIndicator from "../loading/loading";
import SmartSearchProvider from "../../api/providers/SmartSearchProvider";
import SearchKeyboard from "./SearchKeyboard";

// The smart search UI itself, usable either as a full page (see
// smartSearchPage.tsx, the /smartSearch route) or embedded inline under
// Home's header — where selecting the search icon shows this without
// navigating away and losing the header row.
const SmartSearchPanel = (props: {
    onEscape: () => void;
    onUp?: () => void;
    // Same imperative-handle pattern as MoviesPanel/ServicesPanel — a plain
    // object this component attaches a focusEntry() method to, so a parent
    // (Home) can send focus into this panel's content, e.g. when pressing
    // Down from the header while this panel is open.
    panelRef?: { focusEntry?: () => void };
}) => {
    const navigate = useNavigate();
    // Imperative handle SearchKeyboard attaches focusFirstKey()/
    // getLastFocusedKey() to (see the same pattern as MoviesPanel's
    // panelRef, and PinKeyboard's keyboardRef elsewhere in this app).
    const keyboardHandle: { focusFirstKey?: () => void; getLastFocusedKey?: () => any } = {};

    // State management
    const [inputValue, setInputValue] = createSignal("");
    const [isInitializing, setIsInitializing] = createSignal(true);
    const [isSearching, setIsSearching] = createSignal(false);
    const [hasError, setHasError] = createSignal(false);
    const [searchResults, setSearchResults] = createSignal([]);
    const [searchInfo, setSearchInfo] = createSignal({
        totalMovies: 0,
        categories: [],
        searchedCategories: 0,
        totalCategories: 0
    });
    const [showLoading, setShowLoading] = createSignal(true);
    const [isNavigating, setIsNavigating] = createSignal(false);

    let gridRef: any;
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;
    let mainContentRef: any;
    let loadingScreenRef: any;
    let resultsContainerRef: any;
    let typingIndicatorRef: any;

    const smartSearch = SmartSearchProvider.getInstance();

    // Grid configuration
    const ITEMS_PER_ROW = 4;
    const ITEM_WIDTH = 258;
    const ITEM_HEIGHT = 404;
    const GAP = 20;

    // Helper function for smooth navigation with fade out
    const handleNavigate = (path: string) => {
        if (isNavigating()) return;
        setIsNavigating(true);

        gsap.to(mainContentRef, {
            alpha: 0,
            duration: 0.25,
            ease: "power2.inOut",
            onComplete: () => {
                navigate(path);
                setIsNavigating(false);
            }
        });
    };

    const handleEscape = () => {
        if (isInitializing() || isNavigating()) return true;
        setIsNavigating(true);

        gsap.to(mainContentRef, {
            alpha: 0,
            duration: 0.25,
            ease: "power2.inOut",
            onComplete: () => {
                props.onEscape();
                setIsNavigating(false);
            }
        });

        // Stop this Escape from also bubbling up to an ancestor's onEscape —
        // matters when this panel is embedded inside Home's tree rather than
        // mounted as its own route (see the same fix on SettingsPanel).
        return true;
    };

    // Convert flat array to grid rows
    const getGridRows = () => {
        const results = searchResults();
        const rows = [];
        for (let i = 0; i < results.length; i += ITEMS_PER_ROW) {
            rows.push(results.slice(i, i + ITEMS_PER_ROW));
        }
        return rows;
    };

    // Animate results in
    const animateResultsIn = () => {
        const rows = getGridRows();
        if (rows.length === 0 || !resultsContainerRef) return;

        // Fade in the container first
        gsap.fromTo(resultsContainerRef,
            { alpha: 0 },
            {
                alpha: 1,
                duration: 0.3,
                ease: "power2.out"
            }
        );
    };

    // Initialize search (load first 3 categories)
    const initializeSearch = async () => {
        console.log("🚀 Starting search initialization...");
        setIsInitializing(true);
        setHasError(false);

        try {
            const initResult = await smartSearch.initializeSearch();

            setSearchInfo({
                totalMovies: initResult.totalMovies,
                categories: initResult.categories,
                searchedCategories: 0,
                totalCategories: 0
            });

            // Show initial movies (empty search)
            const initialResults = await smartSearch.smartSearch("", 100);
            setSearchResults(initialResults.results);

            console.log(`✅ Search initialized: ${initResult.totalMovies} movies loaded`);
            setIsInitializing(false);

            // Animate results in after a short delay
            setTimeout(() => {
                animateResultsIn();
                keyboardHandle.focusFirstKey?.();
            }, 100);

        } catch (error) {
            console.error("❌ Search initialization failed:", error);
            setHasError(true);
            setIsInitializing(false);
        }
    };

    // Perform smart search
    const performSearch = async (query: string) => {
        setIsSearching(true);

        // Show typing indicator with animation
        if (typingIndicatorRef) {
            gsap.to(typingIndicatorRef, {
                alpha: 1,
                duration: 0.2,
                ease: "power2.out"
            });
        }

        // Fade out results while searching
        if (resultsContainerRef) {
            gsap.to(resultsContainerRef, {
                alpha: 0.3,
                duration: 0.2,
                ease: "power2.out"
            });
        }

        try {
            const searchResult = await smartSearch.smartSearch(query, 50);

            setSearchResults(searchResult.results);
            setSearchInfo(prev => ({
                ...prev,
                searchedCategories: searchResult.searchedCategories,
                totalCategories: searchResult.totalCategories
            }));

            console.log(`🔍 Search "${query}": ${searchResult.results.length} results from ${searchResult.searchedCategories} categories`);

            // Hide typing indicator
            if (typingIndicatorRef) {
                gsap.to(typingIndicatorRef, {
                    alpha: 0,
                    duration: 0.2,
                    ease: "power2.out"
                });
            }

            // Animate results in
            setTimeout(() => {
                animateResultsIn();
            }, 50);

        } catch (error) {
            console.error("Search error:", error);
            setSearchResults([]);

            if (typingIndicatorRef) {
                gsap.to(typingIndicatorRef, {
                    alpha: 0,
                    duration: 0.2,
                    ease: "power2.out"
                });
            }
        } finally {
            setIsSearching(false);
        }
    };

    // Debounced search effect
    createEffect(() => {
        const query = inputValue();

        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Don't search while initializing
        if (isInitializing()) return;

        searchTimeout = setTimeout(() => {
            performSearch(query);
        }, 500);
    });

    onMount(() => {
        console.log("🚀 Smart search component mounted");
        setGlobalBackground("#0A1C34");
        setInputValue("");

        if (props.panelRef) {
            props.panelRef.focusEntry = () => keyboardHandle.focusFirstKey?.();
        }

        // GSAP Timeline for page entrance
        const timeline = gsap.timeline();

        timeline.to(loadingScreenRef, {
            alpha: 0,
            duration: 0.3,
            ease: "power2.out",
            delay: 0.4,
            onComplete: () => {
                setShowLoading(false);
            }
        });

        timeline.to(mainContentRef, {
            alpha: 1,
            duration: 0.4,
            ease: "power2.out"
        }, "-=0.15");

        initializeSearch();
    });

    onCleanup(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
    });

    // Helper function to handle keypad interactions
    const handleKeypadInteraction = (callback: () => void) => {
        if (!isInitializing() && !hasError()) {
            callback();
        }
    };

    // Input handling
    const addCharacter = (char: string) => {
        setInputValue(prev => prev + char);
    };

    const removeCharacter = () => {
        setInputValue(prev => prev.slice(0, -1));
    };

    const addSpace = () => {
        setInputValue(prev => prev + " ");
    };

    // Status text for user feedback
    const getStatusText = () => {
        if (isInitializing()) return "Loading movies...";
        if (isSearching()) return "Searching...";
        if (inputValue().trim()) {
            return `Movies (${searchResults().length})`;
        }
        return `Movies (${searchInfo().totalMovies})`;
    };

    return (
        <View color={hexColor("#000000")}>
            {/* Loading Screen */}
            <Show when={showLoading()}>
                <View
                    ref={loadingScreenRef}
                    width={1920}
                    height={1080}
                    color={hexColor("#000000")}
                    alpha={1}
                    style={{ zIndex: 1000, position: "absolute" }}
                >
                    <View x={900} y={400}>
                        <LoadingIndicator isCategoriesIndicator={false} />
                    </View>
                </View>
            </Show>

            {/* Main Content */}
            <View
                ref={mainContentRef}
                alpha={0}
                width={1920}
                onEscape={handleEscape}
                height={1700}
                positionAbsolute={0}
            >
                {/* Error Overlay */}
                <Show when={hasError()}>
                    <View
                        width={1920}
                        height={1700}
                        color={hexColor("#000000")}
                        alpha={0.8}
                        positionAbsolute={0}
                        style={{ zIndex: 1000 }}
                    >
                        <View x={1160} y={300} style={{ textAlign: 'center' }}>
                            <View x={-50} y={0} width={100} height={100}>
                                <Text fontSize={80} color={hexColor("#FF6B6B")}>⚠️</Text>
                            </View>
                            <Text
                                x={-300}
                                y={120}
                                fontSize={42}
                                color={hexColor("#FFFFFF")}
                                fontWeight="bold"
                                style={{ textAlign: 'center' }}
                            >
                                There is a problem at the moment
                            </Text>
                            <Text
                                x={-150}
                                y={180}
                                fontSize={32}
                                color={hexColor("#CCCCCC")}
                                style={{ textAlign: 'center' }}
                            >
                                Please try again later
                            </Text>
                        </View>
                    </View>
                </Show>

                <View style={{ opacity: isInitializing() || hasError() ? 0.3 : 1 }}>
                    <SearchKeyboard
                        inputValue={inputValue}
                        isInitializing={isInitializing}
                        hasError={hasError}
                        onAddCharacter={addCharacter}
                        onAddSpace={addSpace}
                        onRemoveCharacter={removeCharacter}
                        onUp={() => props.onUp?.()}
                        resultsGridRef={() => gridRef}
                        keyboardRef={keyboardHandle}
                    />
                </View>

                {/* Status and Results */}
                <View x={650} y={260}>
                    <Text fontSize={30} style={{ fontWeight: "bold" }} color={isSearching() ? hexColor("#B9B9BE") : hexColor("#ffffff")}>
                        {getStatusText()}
                    </Text>

                    {/* Typing Indicator */}
                    <View ref={typingIndicatorRef} x={300} y={-10} alpha={0}>
                        <LoadingIndicator isCategoriesIndicator={false} />
                    </View>
                </View>

                {/* Movies Display - GRID LAYOUT */}
                <View y={330} x={650} clipping>
                    <Show when={!isInitializing() && !hasError()}>
                        <View ref={resultsContainerRef} alpha={1}>
                            <Show when={searchResults().length === 0 && inputValue().trim()}>
                                <Text x={100} y={50} fontSize={28} color={hexColor("#B9B9BE")}>No movies found for "{inputValue()}"</Text>
                            </Show>

                            <Show when={searchResults().length > 0}>
                                <LazyUp
                                    component={Column}
                                    onLeft={() => handleKeypadInteraction(() => keyboardHandle.getLastFocusedKey?.()?.setFocus())}
                                    onup={() => handleKeypadInteraction(() => props.onUp?.())}
                                    ref={(el) => (gridRef = el)}
                                    scroll="always"
                                    upCount={3}
                                    gap={GAP}
                                    each={getGridRows()}
                                >
                                    {(rowItems: any) => (
                                        <Row gap={GAP} height={400}>
                                            <For each={rowItems()}>
                                                {(item: any) => (
                                                    <View
                                                        onEnter={() => {
                                                            handleKeypadInteraction(() => {
                                                                console.log("Navigating to:", item);
                                                                handleNavigate(`/entity/movie/${item.stream_id}/${item.category_id}`);
                                                            });
                                                        }}
                                                        style={{
                                                            width: 260,
                                                            height: 390,
                                                            scale: 1,
                                                            borderRadius: 14,
                                                            color: hexColor("#00000000"),
                                                            border: { width: 0, color: hexColor("#ffffff00") },
                                                            transition: {
                                                                scale: { duration: 250, easing: "ease-in-out" },
                                                                border: { duration: 200 }
                                                            },
                                                            focus: {
                                                                color: hexColor("#00000000"),
                                                                border: { width: 4, color: hexColor("#ffffff") }
                                                            }
                                                        }}
                                                    >
                                                        <View
                                                            x={5}
                                                            y={5}
                                                            borderRadius={14}
                                                            width={250}
                                                            height={380}
                                                            src={item.stream_icon}
                                                        />
                                                    </View>
                                                )}
                                            </For>
                                        </Row>
                                    )}
                                </LazyUp>
                            </Show>
                        </View>
                    </Show>
                </View>
            </View>
        </View>
    );
};

export default SmartSearchPanel;
